import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Input, Row, Col, Card, CardHeader, CardBody, Button, Spinner } from "reactstrap";
import {
    createAdditionalAssignmentQuestions,
    createAssignmentQuestions,
    deleteAssignmentQuestions,
    deleteAdditionalAssignmentQuestions as thunkDeleteAdditionalAssignmentQuestions // Renamed to avoid conflict if needed, or use directly
} from "../../../slices/thunks";
import CommonDataTable from "../Table/CommonDataTable";

const AddAssignmentForm = ({ labels, isAddOrEdit, selectedRow, setSelectedRow, onChangeHandler }) => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation(); // Make sure i18n is extracted if used in dropdowns, or t is enough
    const [focused, setFocused] = useState({});
    const [formData, setFormData] = useState(selectedRow);
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [selectedQuestionTypeFilter, setSelectedQuestionTypeFilter] = useState("");
    const [selectedDifficultyFilter, setSelectedDifficultyFilter] = useState("");
    const [selectedTopicFilter, setSelectedTopicFilter] = useState("");
    
    const today = new Date().toISOString().split("T")[0];
    
    const { QuestionTypes, Questions, Loading, QuestionTableColumnNames, DifficultyLevels, TopicsList } = useSelector((state) => ({
        QuestionTypes: state.Common.questionTypes,
        DifficultyLevels: state.Common.difficultyLevel, 
        Questions: state.Questions.questions,
        Loading: state.Questions.loading,
        QuestionTableColumnNames: state.CourseSessions.questionTableColumnNames,
        TopicsList: state.Topics.topicsList,
    }));

    const handleAdd = (question, columnKey) => {
        if (columnKey === "assignment") {
            setSelectedRow((prev) => ({
                ...prev,
                QuestionIDs: [...(prev.QuestionIDs || []), question.ID]
            }));
            dispatch(createAssignmentQuestions({
                SessionID: selectedRow.ID,
                QuestionID: question.ID,
                QuestionTypeID: selectedRow.QuestionTypeID
            }));
        } else { // For "additionalAssignment"
            setSelectedRow((prev) => ({
                ...prev,
                ProblemIDs: [...(prev.ProblemIDs || []), question.ID]
            }));
            dispatch(createAdditionalAssignmentQuestions({
                SessionID: selectedRow.ID,
                QuestionID: question.ID,
            }));
        }
    };

    const handleRemove = (question, columnKey) => {
        if (columnKey === "assignment") {
            setSelectedRow((prev) => ({
                ...prev,
                QuestionIDs: (prev.QuestionIDs || []).filter((questionId) => questionId !== question.ID)
            }));
            dispatch(deleteAssignmentQuestions({
                SessionID: selectedRow.ID,
                QuestionID: question.ID,
            }));
        } else { // For "additionalAssignment"
            setSelectedRow((prev) => ({
                ...prev,
                ProblemIDs: (prev.ProblemIDs || []).filter((problemId) => problemId !== question.ID)
            }));
            dispatch(thunkDeleteAdditionalAssignmentQuestions({ // Use the thunk
                SessionID: selectedRow.ID,
                QuestionID: question.ID,
            }));
        }
    };

    // Click handler for CommonDataTable's button column
    const assignmentHandleButtonClick = (questionItem, columnKey) => {
        let isCurrentlySelected = false;
        if (columnKey === "assignment") {
            isCurrentlySelected = Array.isArray(selectedRow.QuestionIDs) && selectedRow.QuestionIDs.includes(questionItem.ID);
        } else { // For "additionalAssignment"
            isCurrentlySelected = Array.isArray(selectedRow.ProblemIDs) && selectedRow.ProblemIDs.includes(questionItem.ID);
        }

        if (isCurrentlySelected) {
                handleRemove(questionItem, columnKey);
        } else {
               handleAdd(questionItem, columnKey);
        }
        
    };

    useEffect(() => {
         // Set Topic Filter
        if (selectedRow.TopicID) {
            const topicIdString = selectedRow.TopicID.toString();            
            if (TopicsList && TopicsList.some(topic => topic.ID.toString() === topicIdString)) {
                setSelectedTopicFilter(topicIdString);
            } else {
                setSelectedTopicFilter("");
            }
        } else {
            setSelectedTopicFilter("");
        }

        if (isAddOrEdit === "isEdit") {
            // Set Question Type Filter
            if (selectedRow.QuestionTypeID) {
                const typeIdString = selectedRow.QuestionTypeID.toString();
                if (QuestionTypes && QuestionTypes.some(qt => qt.ID.toString() === typeIdString)) {
                    setSelectedQuestionTypeFilter(typeIdString);
                } else {
                    setSelectedQuestionTypeFilter("");
                }
            } else {
                setSelectedQuestionTypeFilter("");
            }
        } else { 
            setSelectedQuestionTypeFilter("");
        }
    }, [isAddOrEdit, selectedRow.QuestionTypeID, selectedRow.TopicID, QuestionTypes, TopicsList]);

    useEffect(() => {
        let processedQuestions = Questions ? [...Questions] : [];

        // 1. Filter by selected Question Type 
        if (selectedQuestionTypeFilter) {
            processedQuestions = processedQuestions.filter(
                (question) => question.QuestionTypeID == selectedQuestionTypeFilter
            );
        }

        // 2. Filter by selected Difficulty Level 
        if (selectedDifficultyFilter) {
            processedQuestions = processedQuestions.filter(
                (question) => question.DifficultyLevel == selectedDifficultyFilter 
            );
        }

        // 3. Filter by selected Topic
        if (selectedTopicFilter) {
            processedQuestions = processedQuestions.filter(
                (question) => question.TopicID == selectedTopicFilter // Corrected: Compare with question.TopicID
            );
        }

        // 3. Filter by search term
        if (searchTerm) {
            const lowerSearchTerm = searchTerm.toLowerCase();
            processedQuestions = processedQuestions.filter(question =>
                (question.QuestionTitleEn?.toLowerCase().includes(lowerSearchTerm) ||
                 question.QuestionTitleAr?.toLowerCase().includes(lowerSearchTerm) ||
                 question.QuestionDescriptionEn?.toLowerCase().includes(lowerSearchTerm) ||
                 question.QuestionDescriptionAr?.toLowerCase().includes(lowerSearchTerm) ||
                 question.TopicNameEn?.toLowerCase().includes(lowerSearchTerm) || // Assuming TopicNameEn exists for search
                 question.TopicNameAr?.toLowerCase().includes(lowerSearchTerm)   // Assuming TopicNameAr exists for search
                )
            );
        }

        // 5. Augment with button text
        const augmentedData = processedQuestions.map(question => {
            const isAssignmentSelected = Array.isArray(selectedRow.QuestionIDs) && selectedRow.QuestionIDs.includes(question.ID);
            const isAdditionalAssignmentSelected = Array.isArray(selectedRow.ProblemIDs) && selectedRow.ProblemIDs.includes(question.ID);

            let assignmentText = t("Add"); 
            let additionalAssignmentText = t("Add"); 

            if (isAssignmentSelected) {
                assignmentText = t("Remove");
                additionalAssignmentText = ""; 
            } else if (isAdditionalAssignmentSelected) {
                additionalAssignmentText = t("Remove");
                assignmentText = ""; 
            }
            // If neither is selected, both remain t("Add")

            return {
                ...question,
                assignment: assignmentText,
                additionalAssignment: additionalAssignmentText,
            };
        });
        setData(augmentedData);

    }, [Questions, selectedRow.QuestionIDs, selectedRow.ProblemIDs, searchTerm, selectedQuestionTypeFilter, selectedDifficultyFilter, selectedTopicFilter, t]);
    

    return (
        <Row>
            { labels?.map((items) => {
                switch (items?.fieldName || items?.labelName) {
                    case 'text-box':
                        return (
                            <Col key={items?.id} sm={6}>
                                <div className="mb-3">
                                    <label htmlFor={items?.labelName} className="col-form-label">{t(items?.labelName)}{items.required && <small className="text-danger"> *</small>}</label>
                                    <Input
                                        type="text"
                                        className="form-control"
                                        placeholder={t(items.placeholder)}
                                        defaultValue={formData[items?.value]}
                                        name={items?.name}
                                        onChange={(e) => onChangeHandler(e)}
                                        id={items?.labelName}
                                        onBlur={onhandleFocus}
                                        focused={focused[items.labelName] && focused[items.labelName].toString()}
                                        required={items.required} />
                                        <small className="text-danger error-msg">{[items.name] && `${t(items.labelName)} ${t(items.IsRequired)}`}</small>
                                </div>
                            </Col>
                        );

                    case 'textarea-box':
                        return (
                            <Col key={items?.id} sm={6}>
                                <div className="mb-3">
                                    <label htmlFor={items?.labelName} className="col-form-label">{t(items?.labelName)}{items.required && <small className="text-danger"> *</small>}</label>
                                    <Input
                                        rows="4"
                                        type="textarea"
                                        className="form-control"
                                        placeholder={t(items.placeholder)}
                                        defaultValue={formData[items?.value]}
                                        name={items?.name}
                                        onChange={(e) => onChangeHandler(e)}
                                        id={items?.labelName}
                                        onBlur={onhandleFocus}
                                        focused={focused[items.labelName] && focused[items.labelName].toString()}
                                        required={items.required} />
                                        <small className="text-danger error-msg">{[items.name] && `${t(items.labelName)} ${t(items.IsRequired)}`}</small>
                                </div>
                            </Col>
                        );

                    case 'number-box':
                        return (
                            <Col key={items?.id} sm={6}>
                                <div className="mb-3">
                                    <label htmlFor={items?.labelName} className="col-form-label">{t(items?.labelName)}{items.required && <small className="text-danger"> *</small>}</label>
                                    <Input
                                        type="number"
                                        className="form-control"
                                        placeholder={t(items.placeholder)}
                                        defaultValue={formData[items?.value]}
                                        name={items?.name}
                                        onChange={(e) => onChangeHandler(e)}
                                        id={items?.labelName}
                                        onBlur={onhandleFocus}
                                        focused={focused[items.labelName] && focused[items.labelName].toString()}
                                        required={items.required} />
                                    <small className="text-danger error-msg">{[items.name] && `Required ${items?.labelName}`}</small>
                                </div>
                            </Col>
                        )

                    case 'check-box':
                        return (
                            <Col key={items?.id} sm={6}>
                                <div className="mb-3">
                                    <label htmlFor={items?.labelName} className="col-form-label">{t(items?.labelName)}{items.required && <small className="text-danger"> *</small>}</label>
                                    <Input
                                        type="checkbox"
                                        className="form-control"
                                        placeholder={t(items.placeholder)}
                                        defaultChecked={selectedRow[items?.value]}
                                        name={items?.name}
                                        onChange={(e) => onChangeHandler(e)}
                                        id={items?.labelName}
                                        onBlur={onhandleFocus}
                                        focused={focused[items.labelName] && focused[items.labelName].toString()}
                                        required={items.required} />
                                        <small className="text-danger error-msg">{[items.name] && `${t(items.labelName)} ${t(items.IsRequired)}`}</small>
                                </div>
                            </Col>
                        );

                    case 'date':
                        return (
                            <Col key={items.id} sm={6}>
                                <div className="mb-3">
                                    <label htmlFor={items.labelName} className="col-form-label">{t(items.labelName)}</label>
                                    <Input 
                                    type="date" 
                                    className="form-control" 
                                    id={items.labelName} 
                                    name={items.name} 
                                    min={today}
                                    defaultValue={(formData[items.value] || '')?.split('T')[0]}
                                    onChange={(e) => onChangeHandler(e)} 
                                    onBlur={onhandleFocus}
                                    focused={focused[items.labelName] && focused[items.labelName].toString()}
                                    required={items.required} 
                                    />
                                </div>
                            </Col>
                        )

                    case 'time':
                        return (
                            <Col key={items.id} sm={6}>
                                <div className="mb-3">
                                    <label htmlFor={items.labelName} className="col-form-label">{t(items.labelName)}</label>
                                    <Input 
                                    type="time" 
                                    className="form-control" 
                                    id={items.labelName} 
                                    name={items.name} 
                                    min={(Module.StartDate || '')?.split('T')[0]}
                                    defaultValue={(formData[items.value] || '')?.split('T')[0]}
                                    onChange={(e) => onChangeHandler(e)} 
                                    onBlur={onhandleFocus}
                                    focused={focused[items.labelName] && focused[items.labelName].toString()}
                                    required={items.required} 
                                    />
                                </div>
                            </Col>
                        )

                    case 'type-dropdown':
                        return (
                            <Col key={items?.id} sm={6}>
                                <div className="mb-3">
                                    <label htmlFor="country" className="col-form-label">{t(items?.labelName)}</label>
                                    <select className="form-control"
                                        id={items.labelName}
                                        name={items.name}
                                        value={selectedRow[items?.value]}
                                        onChange={(e) => onChangeHandler(e)}
                                        disabled={isAddOrEdit === "isEdit"}
                                        required={items.required}>
                                        <option value="">{t("Choose")}</option>
                                        {
                                            QuestionTypes?.map((item) => {
                                                return (
                                                    <option value={item?.ID} key={item?.ID}>{i18n.language === 'en' ? item.NameEn : item.NameAr}</option>
                                                )
                                            })
                                        }
                                    </select>
                                </div>
                            </Col>
                        );

                    case 'question-list':
                        return (
                            <Col key={items?.id} sm={12}>               
                                        <Card>
                                            <CardHeader>
                                            <Row className="g-4 align-items-center">
                                                <Col lg={6} md={12}> {/* Adjust column spans as needed */}
                                                    <Row className="g-2 align-items-end">
                                                        <Col sm={4} className="mb-2 mb-sm-0">
                                                            <label htmlFor="questionTypeFilter" className="form-label mb-0 me-2">{t("Topic")}</label>
                                                            <Input
                                                                type="select"
                                                                className="form-select"
                                                                id="questionTypeFilter"
                                                                value={selectedTopicFilter}
                                                                onChange={(e) => setSelectedTopicFilter(e.target.value)}
                                                                disabled={true}                                                                
                                                            >
                                                                <option value="">{t("FilterByTopic")}</option>
                                                                {TopicsList?.map((type) => (
                                                                    <option value={type.ID} key={type.ID}>
                                                                        {i18n.language === 'en' ? type.TopicNameEn : type.TopicNameAr}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </Col>
                                                        <Col sm={4} className="mb-2 mb-sm-0">
                                                            <label htmlFor="questionTypeFilter" className="form-label mb-0 me-2">{t("Type")}</label>
                                                            <Input
                                                                type="select"
                                                                className="form-select"
                                                                id="questionTypeFilter"
                                                                value={selectedQuestionTypeFilter}
                                                                onChange={(e) => setSelectedQuestionTypeFilter(e.target.value)}
                                                                // disabled={isAddOrEdit === "isEdit"}
                                                            >
                                                                <option value="">{t("FilterByType")}</option>
                                                                {QuestionTypes?.map((type) => (
                                                                    <option value={type.ID} key={type.ID}>
                                                                        {i18n.language === 'en' ? type.NameEn : type.NameAr}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </Col>
                                                        <Col sm={4} className="mb-2 mb-sm-0">
                                                            <label htmlFor="difficultyFilter" className="form-label mb-0 me-2">{t("Difficulty")}</label>
                                                            <Input
                                                                type="select"
                                                                className="form-select"
                                                                id="difficultyFilter"
                                                                value={selectedDifficultyFilter}
                                                                onChange={(e) => setSelectedDifficultyFilter(e.target.value)}
                                                                // bsSize="sm" // Removed for default height
                                                            >
                                                                <option value="">{t("FilterByDifficulty")}</option>
                                                                {DifficultyLevels?.map((level) => (
                                                                    <option value={level.ID} key={level.ID}>
                                                                        {i18n.language === 'en' ? level.NameEn : level.NameAr}
                                                                    </option>
                                                                ))}
                                                            </Input>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                                <Col lg={5} md={12}>
                                                    <div className="d-flex justify-content-lg-end">
                                                        <div className="search-box w-100">
                                                            <label htmlFor="questionSearch" className="form-label mb-0 me-2">{t("SearchA")}</label>
                                                            <Input
                                                            type="text"
                                                            className="form-control ps-5"
                                                            id="questionSearch"
                                                            placeholder={t("SearchQuestions")}
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            // bsSize="sm" // Removed for default height
                                                            />
                                                            <i className="ri-search-line search-icon-assignment position-absolute"></i>
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                            </CardHeader>
                                            <CardBody>
                                             <div className="listjs-table" id="customerList">
                                                {Loading ? (
                                                    <div className="d-flex justify-content-center align-items-center">
                                                        <Spinner style={{ height: "3rem", width: "3rem" }} className="me-2">
                                                            Loading...
                                                        </Spinner>
                                                    </div>
                                                ) : (
                                                    <CommonDataTable
                                                        data={data} 
                                                        tableColumnNames={QuestionTableColumnNames}
                                                        handleButtonClick={assignmentHandleButtonClick}
                                                    />
                                                )}
                                            </div> 
                                            </CardBody>
                                        </Card>
                            </Col>
                        )

                    default:
                        return null
                }
            })}
        </Row>
    )
}

export default AddAssignmentForm